// @flow
import * as React from 'react';
import styled from 'styled-components';
import { isDefined } from 'utils/functional';
import { Link as ReactRouterLink } from 'components/RouterLink';
import { trackClicked } from 'utils/analytics';
import type { RouterHistory, Location } from 'react-router';
import { getCityStateString } from 'utils/address';
import scrollElementIntoView from 'utils/scrollIntoView';
import {
  SEARCH_HIT_IMAGE_WIDTH_SMALL,
  SEARCH_HIT_IMAGE_WIDTH
} from 'utils/constants/general';
import { H4, BodySmall } from 'components/Text';
import ShowModalContainer from 'containers/ShowModalContainer';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Icon from 'components/Icon';
import Divider from 'components/Divider';
import { LogoImage } from 'components/Image';
import { SmallTile } from 'components/Tile';
import { HoverEffect } from 'components/SearchTile';
import {
  SearchTileContainer,
  SearchTileLink,
  StyledButton,
  ListingRouterLink
} from 'components/SearchHit';
import { dayAndMonth } from 'utils/date';
import type { CurrentUser } from 'api/currentUser';
import { breakpoints, colors, fontWeights } from '../theme';

const Actions = styled(Box)`
  @media (min-width: ${breakpoints[1]}) {
    border-left: 1px solid ${colors.aluminum};
  }
`;

const CtaButton = styled(
  ({
    hit,
    hasBeenContacted,
    loggedIn,
    onClick,
    ...props
  }: {
    hit: Object,
    hasBeenContacted: boolean,
    loggedIn?: boolean,
    onClick: Function
  }) => {
    const asLink = !loggedIn || hasBeenContacted;
    return (
      <StyledButton
        {...props}
        component={asLink && ReactRouterLink}
        to={
          asLink
            ? `${hit.url[USER_LOCALE]}${
                hasBeenContacted ? '' : '?contactModalOpen=t'
              }`
            : ''
        }
        onClick={() => {
          onClick();
          trackClicked('Search results CTA button', { type: hit.type });
        }}
      >
        {hasBeenContacted ? getText('View') : getText('Contact Us')}
      </StyledButton>
    );
  }
)`
  &:hover {
    color: white;
    background: ${colors.waterBlue};
  }
`;

const SaveButton = styled(StyledButton)`
  color: ${colors.battleshipGrey};
  svg {
    * {
      fill: none;
      stroke: currentColor;
      stroke-width: 2px;
    }
  }
  &:hover {
    background: ${colors.platinum};
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  }
  ${props =>
    props.saved &&
    `
    svg {
      * {
        fill: ${colors.turtleGreen};
        stroke: ${colors.turtleGreen};
      }
    }
  `}
`;

type InfoItem = {
  label: string,
  value: string
};

const InfoList = styled(
  ({ items, className }: { items: Array<InfoItem>, className: string }) => (
    <ul className={className}>
      {items
        .filter(item => item.value.length > 0)
        .map(item => (
          <li key={item.value.replace(/\s/g, '')}>
            <strong>{item.label}:</strong> {item.value}
          </li>
        ))}
    </ul>
  )
)`
  list-style-type: none;
  padding: 0;
  margin: 0 -10px;
  &:not(:first-child) {
    margin-top: 16px;
  }
  li {
    width: 100%;
    flex: 0 0 auto;
    margin-top: 4px;
    padding: 0 10px;
    color: ${colors.battleshipGrey};
    font-size: 14px;
  }
  strong {
    font-weight: ${fontWeights.semiBold};
  }
  @media (min-width: ${breakpoints[1]}) {
    display: flex;
    flex-wrap: wrap;
    li {
      width: 50%;
    }
  }
`;

type HitWithTrack = {
  trackClick: Function,
  name: string,
  url: Object,
  schoolName: string,
  orgUrl: Object,
  logoHandle: string,
  city: string,
  state: string,
  country: string,
  nbOfStudents: number,
  acceptanceRate: number,
  avgFinancialAid: number,
  applicationDeadline: string,
  isPublicSchool: boolean,
  pathToResults: string
};

const HitInfo = ({
  trackClick,
  name,
  url,
  schoolName,
  city,
  logoHandle,
  state,
  country,
  nbOfStudents,
  acceptanceRate,
  avgFinancialAid,
  applicationDeadline,
  isPublicSchool,
  pathToResults
}: HitWithTrack) => (
  <>
    <Box flex="0 0 auto" pr={[0, 32]} mb={[32, 0]}>
      <LogoImage
        alignItems="flex-start"
        width={[
          SEARCH_HIT_IMAGE_WIDTH_SMALL,
          null,
          null,
          null,
          SEARCH_HIT_IMAGE_WIDTH
        ]}
        fsWidth={SEARCH_HIT_IMAGE_WIDTH}
        handle={logoHandle}
        alt={name}
        initial={schoolName}
      />
    </Box>
    <Box width={['100%', 'auto', null, null]} flex="1 1 auto">
      <Box>
        <H4 mb={0}>
          <ListingRouterLink
            data-qa-id="search-result-link"
            to={{
              pathname: url[USER_LOCALE],
              state: {
                showSearchContext: true,
                pathToResults,
                originatingPathname: pathToResults
              }
            }}
            onClick={() => {
              if (trackClick) trackClick();
            }}
          >
            {name}
          </ListingRouterLink>
          <HoverEffect />
        </H4>
        <BodySmall mb={10} lineHeight={1.5}>
          {schoolName && (
            <>
              {schoolName}
              {' | '}
            </>
          )}
          {getCityStateString({ city, stateCode: state, country })}
        </BodySmall>
      </Box>
      <InfoList
        items={[
          {
            value: nbOfStudents ? nbOfStudents.toLocaleString(USER_LOCALE) : '',
            label: getText('Number of students')
          },
          {
            value: acceptanceRate ? `${acceptanceRate}%` : '',
            label: getText('Acceptance rate')
          },
          {
            value: avgFinancialAid
              ? `$${avgFinancialAid.toLocaleString(USER_LOCALE)}`
              : '',
            label: getText('Average financial aid offer')
          },
          {
            value: applicationDeadline ? dayAndMonth(applicationDeadline) : '',
            label: getText('Next application deadline')
          },
          {
            value: isPublicSchool ? getText('Public') : getText('Private'),
            label: getText('Type')
          }
        ]}
      />
    </Box>
  </>
);

type Props = {
  hit: Object,
  scrollIntoView?: boolean,
  history: RouterHistory,
  location: Location,
  saveProgram?: () => void,
  unsaveProgram: () => void,
  saved: boolean,
  canSave: boolean,
  'data-qa-id'?: string,
  trackClick?: () => any,
  currentUser: CurrentUser,
  updateContactedGradPrograms: Function,
  hasBeenContacted: boolean
};

export default class GradSchoolsSearchHit extends React.PureComponent<Props> {
  hitRef = React.createRef<Element>();

  componentDidMount() {
    if (this.props.scrollIntoView && this.hitRef.current) {
      scrollElementIntoView(this.hitRef.current);
    }
  }

  render() {
    const pathToResults = this.props.location.pathname;
    const loggedIn = isDefined(this.props.currentUser.id);
    return (
      <div
        ref={this.hitRef}
        data-qa-id={this.props['data-qa-id'] || 'search-result'}
      >
        <SearchTileContainer position="relative" my={10}>
          <SearchTileLink
            listingType="GRAD_PROGRAM"
            aria-hidden="true"
            url={this.props.hit.url[USER_LOCALE]}
          />
          <SmallTile
            overflow="visible !important"
            pr={[undefined, null, '16px !important']}
          >
            <Box
              display={['block', 'flex']}
              flexWrap={['wrap', null, 'nowrap']}
            >
              <HitInfo
                trackClick={this.props.trackClick}
                pathToResults={pathToResults}
                {...this.props.hit}
              />
              <Actions
                width={['100%', null, 150]}
                mb={[0, null, -14]}
                mt={[12, null, -14]}
                pl={[0, null, 12]}
                display="flex"
                flexDirection={['row', null, 'column']}
                justifyContent={['flex-start', null, 'center']}
                ml={[0, SEARCH_HIT_IMAGE_WIDTH_SMALL + 30, 'auto']}
                flex="0 0 auto"
              >
                <ShowModalContainer
                  render={showModal => (
                    <CtaButton
                      color={colors.waterBlue}
                      hit={this.props.hit}
                      hasBeenContacted={this.props.hasBeenContacted}
                      loggedIn={loggedIn}
                      onClick={() => {
                        if (!this.props.hasBeenContacted && loggedIn) {
                          showModal('GRAD_PROGRAM_CONTACT', {
                            currentUser: this.props.currentUser,
                            hasBeenContacted: this.props.hasBeenContacted,
                            updateContactedGradPrograms: this.props
                              .updateContactedGradPrograms,
                            programId: this.props.hit.objectID,
                            degreeProgramsOffered: this.props.hit
                              .degreeProgramsOffered
                          });
                        }
                      }}
                    />
                  )}
                />
                {this.props.canSave && (
                  <>
                    <Hide xs sm>
                      <Divider opacity="0.2" />
                    </Hide>
                    <SaveButton
                      ml={[16, null, 0]}
                      saved={this.props.saved}
                      onClick={e => {
                        e.stopPropagation();
                        if (this.props.saved) {
                          this.props.unsaveProgram();
                        } else if (this.props.saveProgram) {
                          this.props.saveProgram();
                        }
                      }}
                      data-qa-id={
                        this.props.saved ? 'unsave-listing' : 'save-listing'
                      }
                    >
                      <Icon name="heart" width={13} />
                      {this.props.saved ? getText('Unsave') : getText('Save')}
                    </SaveButton>
                  </>
                )}
              </Actions>
            </Box>
          </SmallTile>
        </SearchTileContainer>
      </div>
    );
  }
}
